"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.armAngle = armAngle;
exports.headHeightForCamera = headHeightForCamera;
exports.headHeightForLaunch = headHeightForLaunch;
exports.camera = camera;
exports.poseFromCamera = poseFromCamera;
/**
 * The trainer's lift arm and camera: where the camera is for a pose, the pose
 * a localized camera implies, and the head heights that put the camera or the
 * launch point at a given height.
 *
 * The arithmetic follows v1's TrainerSim step for step (including its trip
 * through millimeters), so results are bit-identical to it.
 */
const coordinates_1 = require("./coordinates");
const trainer_1 = require("./trainer");
/** The lift arm's angle above level, in degrees, at a head height (v1 GetArmAngle). */
function armAngle(geometry, headHeight) {
    const head = (0, trainer_1.headInLift)(geometry, headHeight);
    return (Math.atan2(head.z, geometry.liftArmLength + head.y) * 180) / Math.PI;
}
/** The head height that puts the camera at `cameraHeight` (v1 cameraHeight2HeadHeight). */
function headHeightForCamera(geometry, cameraHeight) {
    return cameraHeight - geometry.cameraOffsetFromHead.z;
}
/**
 * The head height that puts a level launch point at `launchHeight` (v1
 * launchHeight2HeadHeight). v1 reads defaultLaunchPointOffset; profiles
 * check that its height equals launchOriginOffset's.
 */
function headHeightForLaunch(geometry, launchHeight) {
    return launchHeight - geometry.launchOriginOffset.z;
}
/**
 * The camera in the trainer's frame, for a camera `cameraZ` millimeters up
 * (v1 _getCameraLocationInTrainerCoords). Throws if the arm cannot put it there.
 */
function cameraInTrainer(geometry, cameraZ) {
    const { liftPivot, liftArmLength: arm, cameraOffsetFromHead: offset } = geometry;
    const head = (0, trainer_1.headInLift)(geometry, headHeightForCamera(geometry, cameraZ / 1000.0));
    return {
        x: head.x + offset.x + liftPivot.x,
        y: head.y + offset.y + (liftPivot.y + arm),
        z: head.z + offset.z + liftPivot.z,
    };
}
/**
 * The camera, in localization coordinates, of a trainer standing at `pose`
 * (v1 _computeLocalizedCameraPosition). Roll and pitch are the mounting's,
 * level and cameraDownwardPitch below it, unless `keep` carries the ones
 * vision measured. Throws on a head height the arm cannot reach.
 */
function camera(profile, pose, keep) {
    const { geometry } = profile.trainer;
    const cameraZ = (pose.headHeight + geometry.cameraOffsetFromHead.z) * 1000.0;
    const { x, y, z } = (0, coordinates_1.toLocalization)(profile.venue, (0, coordinates_1.fromTrainerFrame)(cameraInTrainer(geometry, cameraZ), pose));
    const { roll, pitch } = keep ?? { roll: 0, pitch: 90 - geometry.cameraDownwardPitch };
    return { x, y, z, roll, pitch, yaw: pose.yaw };
}
/**
 * The pose of a trainer whose camera vision localized at `localized` (v1
 * SetPositionLocalized). The camera's roll and pitch do not enter. Throws if
 * the arm cannot hold the camera at that height.
 */
function poseFromCamera(profile, localized) {
    const { geometry } = profile.trainer;
    const inTrainer = (0, trainer_1.rotateAboutZ)(cameraInTrainer(geometry, localized.z), -localized.yaw);
    const atCourt = (0, coordinates_1.fromLocalization)(profile.venue, localized);
    return {
        x: atCourt.x - inTrainer.x,
        y: atCourt.y - inTrainer.y,
        yaw: localized.yaw,
        headHeight: headHeightForCamera(geometry, localized.z / 1000.0),
    };
}
